import { get, post } from 'utils/fetch'
import { apiPath } from 'utils/server-data'

async function fetchCards(options) {
  options = Object.assign({}, options, {
    params: {
      productNames: options.productNames,
    },
  })
  const res = await get(apiPath('cards'), options)
  return res.cards
}

async function fetchCard(cardId, options) {
  options = Object.assign({}, options, {
    params: {
      fields: options.includeFields,
    },
  })
  const res = await get(apiPath(`cards/${cardId}`), options)
  return res.card
}

async function showCard(cardId, options) {
  options = Object.assign({}, options, {
    data: {
      isRetry: options?.isRetry,
    },
  })
  const res = await post(apiPath(`cards/${cardId}/show-card`), options)
  return res.classifiedCardData
}

async function applyCard(product, options) {
  options = Object.assign({}, options, {
    data: {
      product,
      entryPoint: options.entryPoint,
      flowInstanceId: options.flowInstanceId,
      supportedFlowFeatures: options.supportedFlowFeatures,
      isRetry: options.isRetry,
    },
  })
  const res = await post(apiPath(`cards/applications`), options)
  return res
}

async function createCard(payload, options) {
  const { productName: product, userPreferences, entryPoint } = payload
  options = Object.assign({}, options, {
    data: {
      product,
      userPreferences,
      entryPoint,
      fptiHeader: options.fptiHeader,
      isRetry: options.isRetry,
    },
  })
  const res = await post(apiPath(`cards`), options)
  return res
}

async function cancelCard(cardId, options) {
  const res = await post(apiPath(`cards/${cardId}/cancel`), options)
  return res.card
}

async function changePin(cardId, data, options) {
  options = Object.assign({}, options, {
    data,
  })
  const res = await post(apiPath(`cards/${cardId}/change-pin`), options)
  return res
}

async function getPinDetails(cardId, options) {
  options = Object.assign({}, options, {
    params: {
      cardId,
    },
  })
  const res = await get(apiPath(`cards/${cardId}/get-pin-details`), options)
  return res
}

async function changeCvv(cardId, cvv, options) {
  options = Object.assign({}, options, {
    data: {
      cvv,
    },
  })
  await post(apiPath(`cards/${cardId}/change-cvv`), options) //NOTE: Response - 204 No Content.
}

async function viewPin(cardId, cvv, options) {
  options = Object.assign({}, options, {
    data: {
      cvv,
    },
  })
  const res = await post(apiPath(`cards/${cardId}/view-pin`), options)
  return res
}

async function fetchFundingSummary(cardId, options) {
  options = Object.assign({}, options, {
    params: {
      productName: options.productName,
    },
  })
  const res = await get(apiPath(`cards/${cardId}/funding-summary`), options)
  return options.v2 ? res : res.funding
}

async function fetchFundingDetails(dimension, options) {
  options = Object.assign({}, options, {
    data: {
      returnUri: options.returnUri,
      flow: options.flow,
      product: options.product,
      entryPoint: options.entryPoint,
      supportedFlowFeatures: options.supportedFlowFeatures,
      fptiHeader: options.fptiHeader,
      intent: options.intent,
    },
  })
  const res = await post(apiPath(`cards/${dimension}/funding-details`), options)
  return res.funding
}

async function setFunding(cardId, userPreferences, options) {
  options = Object.assign({}, options, {
    data: {
      cardId,
      userPreferences,
      product: options.product,
      fptiHeader: options.fptiHeader,
      isRetry: options.isRetry,
    },
  })
  const res = await post(apiPath(`cards/${cardId}/set-funding`), options)
  return res
}

async function removeFunding(cardId, options) {
  const res = await post(apiPath(`cards/${cardId}/remove-funding`), options)
  return res.funding
}

async function deactivateCard({ cardId, productName, touchpoints }, options) {
  options = Object.assign({}, options, {
    data: {
      fptiHeader: options?.fptiHeader,
      recommendationContext: {
        touchpoint_details: touchpoints.map((code) => ({ code, message_count: 10 })),
      },
      productName,
    },
  })
  const res = await post(apiPath(`cards/${cardId}/deactivate`), options)
  return res
}

async function reactivateCard({ cardId, productName, touchpoints }, options) {
  options = Object.assign({}, options, {
    data: {
      fptiHeader: options?.fptiHeader,
      recommendationContext: {
        touchpoint_details: touchpoints.map((code) => ({ code, message_count: 10 })),
      },
      productName,
    },
  })
  const res = await post(apiPath(`cards/${cardId}/reactivate`), options)
  return res
}

async function reportLostCard(cardId, shippingAddressId, options) {
  options = {
    ...options,
    data: {
      shippingAddressId,
    },
  }
  return await post(apiPath(`cards/${cardId}/report-lost`), options)
}

async function reissueCard(cardId, shippingAddressId, subOperation, options) {
  options = {
    ...options,
    data: {
      shippingAddressId,
      subOperation,
    },
  }
  return await post(apiPath(`cards/${cardId}/reissue`), options)
}

async function initActivation(
  productName,
  nonce,
  options: { cardId?: string } & Record<string, unknown> = {},
) {
  const { cardId, ...requestOptions } = options
  const mergedOptions = Object.assign({}, requestOptions, {
    data: {
      productName,
      nonce,
      ...(cardId ? { cardId } : {}),
    },
  })
  const res = await post(apiPath('cards/init-activate'), mergedOptions)
  return res
}

async function activateCard(
  {
    cardId,
    cvv,
    expiryDate = undefined,
    dob = undefined,
    ssn = undefined,
    pin = undefined,
    productName,
    autoSubmit,
    nonce,
  },
  options,
) {
  options = Object.assign({}, options, {
    data: {
      cardId,
      ...(cvv ? { cvv } : {}),
      ...(expiryDate ? { expiryDate } : {}),
      ...(dob ? { dob } : {}),
      ...(ssn ? { ssn } : {}),
      ...(pin ? { pin } : {}),
      productName,
      autoSubmit,
      nonce,
      isRetry: options.isRetry,
    },
  })

  const res = await post(apiPath(`cards/${cardId}/activate`), options)
  return res
}

async function initEnrollmentPreEligibility(
  { productName, addressId, recommendationContext, includeFields = '', intent = '' },
  options,
) {
  options = Object.assign({}, options, {
    data: {
      productName,
      addressId,
      recommendationContext,
      isRetry: options.isRetry,
      includeFields,
      intent,
      loyaltyEnrollmentStatus: 'enrolled',
    },
  })

  // Keep only specific query parameters in the URL for fpti tracking
  const queryString = window.location.search
  const urlParams = new URLSearchParams(queryString)
  const paramsToKeep = ['clickref', 'pubid', 'pubname', 'flow', 'entrypoint']
  const newUrlParams = new URLSearchParams()
  paramsToKeep.forEach((param) => {
    if (urlParams.has(param)) {
      newUrlParams.set(param, urlParams.get(param))
    }
  })

  const res = await post(apiPath(`cards/init-enroll-preeligibility?${newUrlParams}`), options)
  return res
}

async function fetchXoPPDCContent(recommendationContext) {
  const options = {
    data: {
      productName: 'consumer_debit_card',
      recommendationContext,
      // loyaltyEnrollmentStatus: '', // Not sending this field as user may or may not be enrolled in loyalty program
    },
  }

  const res = await post(apiPath('cards/fetch-recommendations'), options)
  return res
}

async function enrollLoyaltyMember(options) {
  const res = await post(apiPath('cards/loyalty-enroll-member'), options)
  return res
}

async function initEnrollment(
  { productName, addressId, recommendationContext, includeFields = '', intent = '' },
  options,
) {
  options = Object.assign({}, options, {
    data: {
      productName,
      addressId,
      recommendationContext,
      isRetry: options.isRetry,
      includeFields,
      intent,
    },
  })

  // Keep only specific query parameters in the URL for fpti tracking
  const queryString = window.location.search
  const urlParams = new URLSearchParams(queryString)
  const paramsToKeep = ['clickref', 'pubid', 'pubname', 'flow', 'entrypoint']
  const newUrlParams = new URLSearchParams()
  paramsToKeep.forEach((param) => {
    if (urlParams.has(param)) {
      newUrlParams.set(param, urlParams.get(param))
    }
  })

  const res = await post(apiPath(`cards/init-enroll?${newUrlParams}`), options)
  return res
}

async function applyForEnrollment(productName, options) {
  options = Object.assign({}, options, {
    data: { productName },
  })
  const res = await post(apiPath('cards/apply-enroll'), options)
  return res
}

async function enrollCard({ productName, addressId }, options) {
  options = Object.assign({}, options, {
    data: { productName, addressId },
  })
  const res = await post(apiPath('cards/enroll'), options)
  return res
}

async function enrollCardGB({ selectedFundingInstrumentId, productName }, options) {
  options = Object.assign({}, options, {
    data: { selectedFundingInstrumentId, productName },
  })
  const res = await post(apiPath('cards/enroll'), options)
  return res
}

async function enrollCardXO({ productName, offerUnitId, touchpointId }, options) {
  options = Object.assign({}, options, {
    data: {
      productName,
      offerUnitId,
      touchpointId,
    },
  })
  const res = await post(apiPath('cards/enroll-ppdcxo'), options)
  return res
}

async function manageCard(productName, touchpoints, options) {
  options = Object.assign({}, options, {
    data: {
      recommendationContext: {
        touchpoint_details: touchpoints.map((code) => ({ code, message_count: 10 })),
      },
      productName,
    },
  })
  const res = await post(apiPath(`cards/manage`), options)
  return res
}

async function updateNotificationPreferences({ categoryId, channel, enabledFlag }, options) {
  options = Object.assign({}, options, {
    data: {
      categoryId,
      channel,
      enabledFlag,
    },
  })
  const res = await post(apiPath(`cards/notification-preferences`), options)
  return res
}

export {
  fetchCards,
  fetchCard,
  showCard,
  applyCard,
  createCard,
  cancelCard,
  changePin,
  getPinDetails,
  changeCvv,
  viewPin,
  fetchFundingSummary,
  fetchFundingDetails,
  setFunding,
  removeFunding,
  deactivateCard,
  reactivateCard,
  reportLostCard,
  reissueCard,
  initActivation,
  activateCard,
  initEnrollment,
  initEnrollmentPreEligibility,
  fetchXoPPDCContent,
  enrollLoyaltyMember,
  applyForEnrollment,
  enrollCard,
  enrollCardGB,
  enrollCardXO,
  manageCard,
  updateNotificationPreferences,
}
